'use client';

import Link from 'next/link';
import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';

import type { PopoverProps } from '@radix-ui/react-popover';

import { cn } from '~/utils';
import { useIsMobile } from '~/hooks/useResponsive';
import { useChatNotification } from '~/hooks/useChatNotification';
import { NotificationCard } from '~/layouts/MainLayout/NotificationDropdown';
import {
  Dot,
  Text,
  Show,
  Tabs,
  HStack,
  Popover,
  TabsList,
  TabsContent,
  TearDropIcon,
  PopoverContent,
  PopoverTrigger,
} from '~/components';

export function ChatDropdown({ onOpenChange, ...props }: PopoverProps) {
  const t = useTranslations('Home');
  const router = useRouter();
  const isMobile = useIsMobile();
  const { realtimeNotifications, haveUnreadChat } = useChatNotification();

  const handleNotiClick = useCallback(
    (url?: string) => {
      if (url) router.push(url);
    },
    [router],
  );

  return (
    <Popover onOpenChange={onOpenChange} {...props}>
      <PopoverTrigger>
        <div className='relative'>
          <TearDropIcon className='size-4 cursor-pointer' />
          <Show when={haveUnreadChat}>
            <Dot
              variant='destructive'
              className='size-1 absolute top-0 right-0.5'
            />
          </Show>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align={isMobile ? 'center' : 'end'}
        className={cn(
          'py-4 px-0 z-2000 w-[280px] max-h-[400px] border border-[#ECECEC] rounded-2xl shadow-[0px_4px_30px_0px_rgba(51,51,51,0.1)]',
          'md:w-[480px]',
        )}
      >
        <HStack className='items-center justify-between px-4'>
          <Text className='p-0 font-medium'>{t('notifications')}</Text>
          <Link
            href='/chat'
            className='ml-auto p-0 right-4 text-blueBonnet-500 font-bold transition-colors hover:text-blueBonnet-600'
          >
            {t('openChat')}
          </Link>
        </HStack>
        <Tabs defaultValue='chat' className='w-full'>
          <TabsList variant='underline' className='h-4' />
          <TabsContent value='chat'>
            <div className='max-h-[250px] overflow-y-auto'>
              <Show
                when={realtimeNotifications.length > 0}
                fallback={
                  <Text size='sm' className='text-center text-dimGray-500 py-2'>
                    {t('noNewNotifications')}
                  </Text>
                }
              >
                {realtimeNotifications.map(
                  ({ id, message, createdAt, read }) => (
                    <NotificationCard
                      key={id}
                      read={read}
                      time={createdAt}
                      message={message}
                      onClick={() => handleNotiClick(`/chat/${id}`)}
                    />
                  ),
                )}
              </Show>
            </div>
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
